import React, { useMemo } from "react";
import { Table, Input } from "reactstrap";

/**
 * Reusable grid for the 5 requirement-response tabs (Key / Sales / Engineering / Materials / Quality).
 *
 * Editor selection per row follows the legacy view_CustomRowCellEdit handler (Pass 1 §1.7):
 *   • YesNoR  → radio group with values "1" / "0" / "-1"
 *   • DateR   → date input
 *   • NumberR → numeric text input
 *   • TextR   → text input (also the default)
 *
 * RecordNamePrompt columns (RecordName / StorageLocation / NamingConvention):
 *   • Disabled & gray when row.recordNamePrompt is false (legacy DataGridView.RowCellStyle)
 *   • Blue text when the entry matches the master "default" value (RecordNameD/etc.)
 *
 * The grid is purely controlled — parent owns the rows array.
 * Parent is responsible for permission gating; this component only honors `readOnly`.
 */
export default function RequirementGrid({
    title,
    rows,
    onChangeRow,
    onBulkAssign,
    employees,
    readOnly,
    myEmpId,
    isAuthor,
    authorSubmitted,
    // Contract Review: when true, hide the "assign all to…" bulk dropdown for
    // this grid. Used on Key Details, where responsibility is auto-assigned to
    // the CR creator rather than chosen from a dropdown.
    hideBulkAssign,
    // Contract Review: when true, the per-row Employee cell shows the assigned
    // person's NAME as plain text (no dropdown). Used on Key Details, which is
    // always the author.
    lockEmployeeToName,
}) {
    const empMap = useMemo(() => {
        const m = new Map();
        (employees || []).forEach((e) => m.set(String(e.empId), e));
        return m;
    }, [employees]);

    if (!rows) return null;

    return (
        <div className="cr-requirement-grid">
            {title && <h6 className="text-muted mb-2">{title}</h6>}
            <Table size="sm" bordered responsive className="align-middle">
                <thead className="table-light">
                    <tr>
                        <th style={{ width: "26%" }}>Question</th>
                        <th style={{ width: "16%" }}>
                            <div>Employee</div>
                            {!readOnly && isAuthor && onBulkAssign && !hideBulkAssign && (
                                <Input
                                    type="select"
                                    bsSize="sm"
                                    className="mt-1"
                                    defaultValue=""
                                    title="Assign this person to every row"
                                    onChange={(e) => {
                                        const v = e.target.value ? Number(e.target.value) : null;
                                        onBulkAssign(v);
                                        e.target.value = ""; // reset so it can be re-applied
                                    }}
                                >
                                    <option value="">— assign all to… —</option>
                                    {(employees || []).map((emp) => (
                                        <option key={emp.empId} value={emp.empId}>
                                            {emp.name}
                                        </option>
                                    ))}
                                </Input>
                            )}
                        </th>
                        <th style={{ width: "16%" }}>Response</th>
                        <th style={{ width: "12%" }}>Record Name</th>
                        <th style={{ width: "12%" }}>Storage Location</th>
                        <th style={{ width: "12%" }}>Naming Convention</th>
                        <th style={{ width: "16%" }}>Notes</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, idx) => (
                        <RequirementRow
                            key={row.id || `new-${idx}`}
                            row={row}
                            onChange={(patch) => onChangeRow(idx, patch)}
                            empMap={empMap}
                            employees={employees}
                            readOnly={readOnly}
                            myEmpId={myEmpId}
                            isAuthor={isAuthor}
                            authorSubmitted={authorSubmitted}
                            lockEmployeeToName={lockEmployeeToName}
                        />
                    ))}
                    {rows.length === 0 && (
                        <tr>
                            <td colSpan={7} className="text-center text-muted py-3">
                                No requirements loaded. Use the &ldquo;Set Defaults&rdquo; tab to load requirements.
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </div>
    );
}

function RequirementRow({ row, onChange, empMap, employees, readOnly, myEmpId, isAuthor, authorSubmitted, lockEmployeeToName }) {
    // Mirrors the ShowingEditor cell gate (Pass 1 §1.6 "roNotMyTasks"). Once author has submitted,
    // only the row's responsible employee (or the author) may edit Response/RecordName/etc.
    const me = Number(myEmpId);
    const rowEmp = empMap.get(String(row.responsible));
    const isMyRow = Number(row.responsible) === me;
    const isMySupv = rowEmp && Number(rowEmp.supervisorId) === me;
    const canEditEmployee = !readOnly && isAuthor;
    const canEditAnswer = !readOnly && (isMyRow || isMySupv);

    // Helper: blue text when answer matches the master default (Pass 1 §1.17 visual hint).
    const matchesDefault = (val, defaultVal) =>
        val && defaultVal && val.toString().trim().toLowerCase() === defaultVal.toString().trim().toLowerCase();

    const recordCellsDisabled = !row.recordNamePrompt;
    const grayBg = { backgroundColor: "#dcdcdc" };

    // -------------------------------------------------------------------------
    // Incomplete-row highlight: red left border + red Response cell when
    // (a) the row has a responsible assigned (otherwise it isn't a question yet),
    // (b) the row is not complete (blank Response, or RecordName trio incomplete),
    // (c) editing is possible (read-only views don't need scolding red).
    // -------------------------------------------------------------------------
    const trimmed = (s) => (s == null ? "" : String(s).trim());
    const responseBlank = !trimmed(row.response);
    const trioIncomplete = row.recordNamePrompt && (
        !trimmed(row.recordName) || !trimmed(row.storageLocation) || !trimmed(row.namingConvention)
    );
    const rowIncomplete = !!row.responsible && (responseBlank || trioIncomplete);
    const rowHighlight = rowIncomplete && !readOnly
        ? { borderLeft: "4px solid #f06548", backgroundColor: "rgba(240,101,72,0.04)" }
        : undefined;
    const responseCellHighlight = rowIncomplete && responseBlank && !readOnly
        ? { boxShadow: "inset 0 0 0 2px #f06548", borderRadius: "0.25rem" }
        : undefined;
    const recordCellMiss = (val) => rowIncomplete && row.recordNamePrompt && !trimmed(val) && !readOnly
        ? { boxShadow: "inset 0 0 0 2px #f06548", borderRadius: "0.25rem" }
        : undefined;

    return (
        <tr style={rowHighlight} title={rowIncomplete ? "This row is incomplete." : undefined}>
            {/* Question — always read-only */}
            <td style={{ whiteSpace: "pre-wrap" }}>{row.questionText}</td>

            {/* Employee — Key Details shows the author's name as plain text
                (no dropdown); other tabs keep the selectable dropdown. */}
            <td>
                {lockEmployeeToName ? (
                    <span className="fw-medium">
                        {rowEmp ? rowEmp.name : (row.responsibleName || "—")}
                    </span>
                ) : (
                    <Input
                        type="select"
                        bsSize="sm"
                        value={row.responsible ?? ""}
                        disabled={!canEditEmployee}
                        onChange={(e) => onChange({ responsible: e.target.value ? Number(e.target.value) : null })}
                    >
                        <option value="">— select —</option>
                        {(employees || []).map((emp) => (
                            <option key={emp.empId} value={emp.empId}>
                                {emp.name}
                            </option>
                        ))}
                    </Input>
                )}
            </td>

            {/* Response — editor type depends on row flags */}
            <td style={responseCellHighlight}>
                <ResponseEditor row={row} onChange={onChange} disabled={!canEditAnswer} />
            </td>

            {/* Record Name */}
            <td style={{ ...(recordCellsDisabled ? grayBg : {}), ...(recordCellMiss(row.recordName) || {}) }}>
                <Input
                    type="text"
                    bsSize="sm"
                    value={row.recordName ?? ""}
                    disabled={recordCellsDisabled || !canEditAnswer}
                    style={{
                        color: matchesDefault(row.recordName, row.recordNameD) ? "blue" : undefined,
                    }}
                    onChange={(e) => onChange({ recordName: e.target.value })}
                    title={row.recordNameD ? `Default: ${row.recordNameD}` : undefined}
                />
            </td>

            {/* Storage Location */}
            <td style={{ ...(recordCellsDisabled ? grayBg : {}), ...(recordCellMiss(row.storageLocation) || {}) }}>
                <Input
                    type="text"
                    bsSize="sm"
                    value={row.storageLocation ?? ""}
                    disabled={recordCellsDisabled || !canEditAnswer}
                    style={{
                        color: matchesDefault(row.storageLocation, row.storageLocationD) ? "blue" : undefined,
                    }}
                    onChange={(e) => onChange({ storageLocation: e.target.value })}
                    title={row.storageLocationD ? `Default: ${row.storageLocationD}` : undefined}
                />
            </td>

            {/* Naming Convention */}
            <td style={{ ...(recordCellsDisabled ? grayBg : {}), ...(recordCellMiss(row.namingConvention) || {}) }}>
                <Input
                    type="text"
                    bsSize="sm"
                    value={row.namingConvention ?? ""}
                    disabled={recordCellsDisabled || !canEditAnswer}
                    style={{
                        color: matchesDefault(row.namingConvention, row.namingConventionD) ? "blue" : undefined,
                    }}
                    onChange={(e) => onChange({ namingConvention: e.target.value })}
                    title={row.namingConventionD ? `Default: ${row.namingConventionD}` : undefined}
                />
            </td>

            {/* Notes — always editable for valid editors */}
            <td>
                <Input
                    type="textarea"
                    bsSize="sm"
                    rows={2}
                    value={row.notes ?? ""}
                    disabled={!canEditAnswer}
                    onChange={(e) => onChange({ notes: e.target.value })}
                />
            </td>
        </tr>
    );
}

/**
 * Editor switching block — mirrors the inplaceEditors array in the legacy form:
 *   inplaceEditors = { riEmployeeEdit, riGroupEdit, riTextEdit, memoEdit, riDateEdit }
 * Index 1 (riGroupEdit) is used when YesNoR; index 4 (riDateEdit) when DateR; otherwise textbox.
 */
function ResponseEditor({ row, onChange, disabled }) {
    if (row.yesNoR) {
        // 3-way radio: Yes=1 / No=0 / n/a=-1 — values exactly as the legacy RadioGroupItem values.
        const v = row.response ?? "";
        const setV = (val) => onChange({ response: val });
        return (
            <div className="d-flex gap-3 align-items-center">
                <label className="form-check d-flex align-items-center gap-1 mb-0">
                    <input
                        type="radio"
                        className="form-check-input"
                        checked={v === "1"}
                        disabled={disabled}
                        onChange={() => setV("1")}
                    />
                    <span>Yes</span>
                </label>
                <label className="form-check d-flex align-items-center gap-1 mb-0">
                    <input
                        type="radio"
                        className="form-check-input"
                        checked={v === "0"}
                        disabled={disabled}
                        onChange={() => setV("0")}
                    />
                    <span>No</span>
                </label>
                <label className="form-check d-flex align-items-center gap-1 mb-0">
                    <input
                        type="radio"
                        className="form-check-input"
                        checked={v === "-1"}
                        disabled={disabled}
                        onChange={() => setV("-1")}
                    />
                    <span>n/a</span>
                </label>
            </div>
        );
    }

    if (row.dateR) {
        // Date input — mirrors riDateEdit with MM/dd/yyyy mask. Stored as MM/dd/yyyy string in legacy.
        // We use native <input type="date"> which uses yyyy-MM-dd internally; coerce on save.
        const isoVal = parseToIsoDate(row.response);
        return (
            <Input
                type="date"
                bsSize="sm"
                value={isoVal}
                disabled={disabled}
                onChange={(e) => onChange({ response: e.target.value ? toMmDdYyyy(e.target.value) : "" })}
            />
        );
    }

    if (row.numberR) {
        return (
            <Input
                type="number"
                bsSize="sm"
                step="any"
                value={row.response ?? ""}
                disabled={disabled}
                onChange={(e) => onChange({ response: e.target.value })}
            />
        );
    }

    // Default: text — riTextEdit / memoEdit. Use textarea for multi-line.
    return (
        <Input
            type="textarea"
            bsSize="sm"
            rows={2}
            value={row.response ?? ""}
            disabled={disabled}
            onChange={(e) => onChange({ response: e.target.value })}
        />
    );
}

// ---- Date helpers ----
function parseToIsoDate(s) {
    if (!s) return "";
    // Already ISO
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    // MM/dd/yyyy
    const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
    if (m) {
        const mm = m[1].padStart(2, "0");
        const dd = m[2].padStart(2, "0");
        return `${m[3]}-${mm}-${dd}`;
    }
    return "";
}
function toMmDdYyyy(iso) {
    if (!iso) return "";
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m) return iso;
    return `${m[2]}/${m[3]}/${m[1]}`;
}
